import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, KeyRound, Trash2, AlertTriangle, ArchiveRestore, History, Eye, EyeOff, Eraser, Users, UserCog, Home } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, StatusBadge } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { api, errMsg, fmtINR, fmtDate } from "../../lib/api";
import { PASSWORD_RULES_HINT } from "../../components/ForgotPasswordDialog";

// Super Admin manages Municipal Admin accounts: edit details + activate/deactivate.
// (Municipal Admins cannot edit or deactivate admin accounts from their own portal.)
function ManageAdminRow({ code, admin, onSaved }) {
  const [form, setForm] = useState({ name: admin.name || "", email: admin.email || "",
    mobile: admin.mobile || "", designation: admin.designation || "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const dirty = form.name !== (admin.name || "") || form.email !== (admin.email || "")
    || form.mobile !== (admin.mobile || "") || form.designation !== (admin.designation || "");
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/platform/municipalities/${code}/admins/${admin.id}`, form);
      toast.success(`${form.name} updated`);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };
  const toggle = async () => {
    try {
      const r = await api.put(`/platform/municipalities/${code}/admins/${admin.id}/toggle`);
      toast.success(`${r.data.name} ${r.data.is_active ? "activated" : "deactivated"}`);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="border rounded-lg p-3 space-y-2" data-testid={`manage-admin-row-${admin.email}`}>
      <div className="flex items-center justify-between gap-2">
        <StatusBadge status={admin.is_active !== false ? "active" : "inactive"} />
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{admin.is_active !== false ? "Active" : "Inactive"}</span>
          <Switch checked={admin.is_active !== false} onCheckedChange={toggle}
            data-testid={`manage-admin-toggle-${admin.email}`} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-xs">Full name</Label>
          <Input value={form.name} onChange={set("name")} data-testid={`manage-admin-name-${admin.email}`} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Designation</Label>
          <Input value={form.designation} onChange={set("designation")} data-testid={`manage-admin-designation-${admin.email}`} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Email</Label>
          <Input type="email" value={form.email} onChange={set("email")} data-testid={`manage-admin-email-${admin.email}`} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Mobile</Label>
          <Input value={form.mobile} onChange={set("mobile")} maxLength={10} data-testid={`manage-admin-mobile-${admin.email}`} />
        </div>
      </div>
      {dirty && (
        <Button size="sm" className="w-full h-8" onClick={save} disabled={busy}
          data-testid={`manage-admin-save-${admin.email}`}>
          {busy ? "Saving..." : "Save changes"}
        </Button>
      )}
    </div>
  );
}

function ManageAdminsDialog({ muni, open, onOpenChange }) {
  const [admins, setAdmins] = useState(null);
  const load = React.useCallback(() => {
    if (muni) api.get(`/platform/municipalities/${muni.code}/admins`).then((r) => setAdmins(r.data)).catch(() => setAdmins([]));
  }, [muni]);
  useEffect(() => {
    if (open && muni) { setAdmins(null); load(); }
  }, [open, muni, load]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="manage-admins-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <UserCog className="h-4 w-4" /> Municipal Admins — {muni?.name}
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground bg-accent/50 rounded-md px-3 py-2">
          Only the Super Admin can edit or deactivate Municipal Admin accounts. The last active admin of a municipality cannot be deactivated.
        </p>
        {!admins ? <Spinner label="Loading admins..." /> : admins.length === 0 ? (
          <p className="text-sm text-muted-foreground">No municipal admin accounts found.</p>
        ) : (
          <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
            {admins.map((a) => <ManageAdminRow key={a.id} code={muni.code} admin={a} onSaved={load} />)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AdminResetDialog({ muni, open, onOpenChange }) {
  const [admins, setAdmins] = useState(null);
  const [adminId, setAdminId] = useState("");
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && muni) {
      setAdmins(null);
      api.get(`/platform/municipalities/${muni.code}/admins`).then((r) => {
        setAdmins(r.data);
        if (r.data.length === 1) setAdminId(r.data[0].id);
      }).catch((e) => toast.error(errMsg(e)));
    }
    if (!open) { setAdminId(""); setPw1(""); setPw2(""); }
  }, [open, muni]);

  const submit = async (e) => {
    e.preventDefault();
    if (!adminId) return toast.error("Select the admin account");
    if (pw1 !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      const r = await api.post(`/platform/municipalities/${muni.code}/admins/${adminId}/reset-password`, { new_password: pw1 });
      toast.success(r.data.message || "Password reset");
      onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  const selected = (admins || []).find((a) => a.id === adminId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="admin-reset-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <KeyRound className="h-4 w-4" /> Reset admin password
          </DialogTitle>
        </DialogHeader>
        {!admins ? <Spinner label="Loading admins..." /> : (
          <form onSubmit={submit} className="space-y-3">
            <p className="text-sm text-muted-foreground">{muni?.name}</p>
            <p className="text-xs text-muted-foreground bg-accent/50 rounded-md px-3 py-2" data-testid="admin-reset-force-change-note">
              The admin will be asked to set their own password at next login.
            </p>
            <div className="space-y-1">
              <Label>Municipal admin account</Label>
              <Select value={adminId} onValueChange={(v) => v && setAdminId(v)}>
                <SelectTrigger data-testid="admin-reset-user-select">
                  <SelectValue placeholder="Select admin">{selected ? `${selected.name} (${selected.email})` : undefined}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {admins.map((a) => (
                    <SelectItem key={a.id} value={a.id} data-testid={`admin-reset-option-${a.email}`}>
                      {a.name} ({a.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>New password</Label>
              <Input type="password" value={pw1} onChange={(e) => setPw1(e.target.value)} required data-testid="admin-reset-new-password-input" />
              <p className="text-[11px] text-muted-foreground">{PASSWORD_RULES_HINT}</p>
            </div>
            <div className="space-y-1">
              <Label>Confirm new password</Label>
              <Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required data-testid="admin-reset-confirm-password-input" />
            </div>
            <Button type="submit" className="w-full" disabled={busy} data-testid="admin-reset-submit-button">
              {busy ? "Resetting..." : "Reset password"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

const empty = { name: "", code: "", ulb_code: "", short_name: "", state: "", district: "", contact_email: "", contact_phone: "",
  admin_name: "", admin_email: "", admin_password: "", seed_demo: false };

function DeleteMuniDialog({ muni, onOpenChange, onDeleted }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setTyped(""); }, [muni]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.delete(`/platform/municipalities/${muni.id}`, { params: { confirm_code: typed } });
      toast.success(`${r.data.name} removed from the portal — its data is retained and recoverable by support`);
      onOpenChange(false);
      onDeleted();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!muni} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="muni-delete-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-destructive">
            <Trash2 className="h-4 w-4" /> Delete municipality
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="text-xs bg-destructive/10 text-destructive rounded-md px-3 py-2 flex gap-2" data-testid="muni-delete-warning">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              <span className="font-semibold">{muni?.name}</span> will disappear from the portal and all
              its logins will be blocked. Its data stays in the database and can be restored by support.
            </span>
          </div>
          <div className="space-y-1">
            <Label>Type the municipality code <span className="font-mono font-semibold">{muni?.code}</span> to confirm</Label>
            <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={muni?.code}
              autoFocus data-testid="muni-delete-confirm-input" />
          </div>
          <Button type="submit" variant="destructive" className="w-full"
            disabled={busy || typed.trim().toLowerCase() !== muni?.code}
            data-testid="muni-delete-submit-button">
            {busy ? "Deleting..." : "Delete municipality"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CleanupMuniDialog({ muni, onOpenChange, onDone }) {
  const [preview, setPreview] = useState(null);
  const [keepIds, setKeepIds] = useState([]);
  const [wipeProps, setWipeProps] = useState(true);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (muni) {
      setPreview(null); setTyped(""); setWipeProps(true); setKeepIds([]);
      api.get(`/platform/municipalities/${muni.code}/cleanup/preview`).then((r) => {
        setPreview(r.data);
        // municipal admins are pre-selected to avoid lockout
        setKeepIds(r.data.users.filter((u) => u.role === "municipal_admin").map((u) => u.id));
      }).catch((e) => toast.error(errMsg(e)));
    }
  }, [muni]);

  const toggleKeep = (id) =>
    setKeepIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  const usersToDelete = preview ? preview.users.length - keepIds.length : 0;
  const hasAdminKept = preview
    ? preview.users.some((u) => keepIds.includes(u.id) && u.role === "municipal_admin" && u.is_active !== false)
    : false;
  const nonZeroCounts = preview
    ? Object.entries(preview.property_data_counts).filter(([, v]) => v > 0)
    : [];

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post(`/platform/municipalities/${muni.code}/cleanup`, {
        keep_user_ids: keepIds, wipe_properties: wipeProps, confirm_code: typed,
      });
      toast.success(`${r.data.name}: ${r.data.users_deleted} user(s) deleted, ${r.data.property_docs_wiped} property record(s) wiped`);
      onOpenChange(false);
      onDone();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!muni} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="muni-cleanup-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-destructive">
            <Eraser className="h-4 w-4" /> Data cleanup — {muni?.name}
          </DialogTitle>
        </DialogHeader>
        {!preview ? <Spinner label="Analysing tenant data..." /> : (
          <form onSubmit={submit} className="space-y-4">
            <div className="text-xs bg-destructive/10 text-destructive rounded-md px-3 py-2 flex gap-2" data-testid="muni-cleanup-warning">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                This <span className="font-semibold">permanently deletes</span> the unchecked users
                {wipeProps ? " and every property with its demands, receipts, payments, assignments, reassessments, surveys and upload batches" : ""}.
                Masters, grievances and service applications are kept. <span className="font-semibold">This cannot be undone.</span>
              </span>
            </div>

            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Users to KEEP ({keepIds.length} of {preview.users.length})</Label>
              <div className="border rounded-lg divide-y max-h-56 overflow-y-auto" data-testid="muni-cleanup-user-list">
                {preview.users.map((u) => (
                  <label key={u.id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-accent/50">
                    <Checkbox checked={keepIds.includes(u.id)} onCheckedChange={() => toggleKeep(u.id)}
                      data-testid={`muni-cleanup-keep-${u.email}`} />
                    <span className="min-w-0 flex-1">
                      <span className="font-medium">{u.name}</span>
                      <span className="text-muted-foreground"> — {u.email}</span>
                    </span>
                    <span className="text-[11px] text-muted-foreground shrink-0 capitalize">{(u.role || "").replace("_", " ")}</span>
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground">
                Unchecked users are deleted ({usersToDelete} will be removed). Keep at least one active Municipal Admin.
              </p>
              {!hasAdminKept && (
                <p className="text-[11px] text-destructive font-medium" data-testid="muni-cleanup-admin-warning">
                  Select at least one active Municipal Admin to keep — otherwise the municipality is locked out.
                </p>
              )}
            </div>

            <div className="border rounded-lg px-3 py-2.5 space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Wipe all properties &amp; related data</p>
                  <p className="text-xs text-muted-foreground">Demands, receipts, payments, assignments, reassessments, surveys, upload batches — PIN &amp; receipt numbering restarts</p>
                </div>
                <Switch checked={wipeProps} onCheckedChange={setWipeProps} data-testid="muni-cleanup-wipe-switch" />
              </div>
              {wipeProps && (
                <div className="flex flex-wrap gap-1.5 pt-1" data-testid="muni-cleanup-counts">
                  {nonZeroCounts.length === 0 ? (
                    <span className="text-[11px] text-muted-foreground">No property data found — nothing to wipe.</span>
                  ) : nonZeroCounts.map(([k, v]) => (
                    <span key={k} className="text-[11px] bg-destructive/10 text-destructive rounded px-1.5 py-0.5 tabular-nums">
                      {preview.labels?.[k] || k}: {v}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-1">
              <Label>Type the municipality code <span className="font-mono font-semibold">{muni?.code}</span> to confirm</Label>
              <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={muni?.code}
                data-testid="muni-cleanup-confirm-input" />
            </div>
            <Button type="submit" variant="destructive" className="w-full"
              disabled={busy || !hasAdminKept || typed.trim().toLowerCase() !== muni?.code}
              data-testid="muni-cleanup-submit-button">
              {busy ? "Cleaning up..." : `Delete ${usersToDelete} user(s)${wipeProps ? ` + wipe ${preview.total_property_docs} record(s)` : ""}`}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Municipalities() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [resetMuni, setResetMuni] = useState(null);
  const [manageMuni, setManageMuni] = useState(null);
  const [deleteMuni, setDeleteMuni] = useState(null);
  const [cleanupMuni, setCleanupMuni] = useState(null);
  const navigate = useNavigate();
  const [removed, setRemoved] = useState([]);
  const [restoringId, setRestoringId] = useState("");
  const [showRemoved, setShowRemoved] = useState(false); // hidden by default — unhide to restore

  const load = () => {
    api.get("/platform/municipalities").then((r) => setItems(r.data)).catch(() => {});
    api.get("/platform/municipalities/removed").then((r) => setRemoved(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const restore = async (m) => {
    setRestoringId(m.id);
    try {
      const r = await api.put(`/platform/municipalities/${m.id}/restore`);
      toast.success(`${r.data.name} restored as Inactive — activate it to re-enable logins`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setRestoringId(""); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/platform/municipalities", form);
      toast.success(`${form.name} onboarded successfully`);
      setOpen(false);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (m) => {
    await api.put(`/platform/municipalities/${m.id}/toggle`);
    toast.success(`${m.name} ${m.is_active ? "deactivated" : "activated"}`);
    load();
  };

  if (!items) return <Spinner />;

  return (
    <div>
      <PageHeader title="Municipalities" subtitle="Onboard and manage municipal corporations">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="onboard-municipality-button"><Plus className="h-4 w-4 mr-2" /> Onboard Municipality</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">Onboard new municipality</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1 col-span-2">
                  <Label>Municipality Name</Label>
                  <Input value={form.name} onChange={set("name")} required placeholder="Nagpur Municipal Corporation" data-testid="muni-name-input" />
                </div>
                <div className="space-y-1">
                  <Label>Code (unique)</Label>
                  <Input value={form.code} onChange={set("code")} required placeholder="nmc" data-testid="muni-code-input" />
                </div>
                <div className="space-y-1">
                  <Label>ULB Code (official)</Label>
                  <Input value={form.ulb_code} onChange={set("ulb_code")} placeholder="GNN-01" data-testid="muni-ulb-code-input" />
                </div>
                <div className="space-y-1">
                  <Label>Short Name</Label>
                  <Input value={form.short_name} onChange={set("short_name")} placeholder="NMC Nagpur" />
                </div>
                <div className="space-y-1">
                  <Label>State</Label>
                  <Input value={form.state} onChange={set("state")} required data-testid="muni-state-input" />
                </div>
                <div className="space-y-1">
                  <Label>District</Label>
                  <Input value={form.district} onChange={set("district")} />
                </div>
                <div className="space-y-1">
                  <Label>Contact Email</Label>
                  <Input type="email" value={form.contact_email} onChange={set("contact_email")} />
                </div>
                <div className="space-y-1">
                  <Label>Contact Phone</Label>
                  <Input value={form.contact_phone} onChange={set("contact_phone")} />
                </div>
              </div>
              <div className="border-t pt-3">
                <p className="text-sm font-medium mb-2">Municipal Admin account</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1 col-span-2">
                    <Label>Admin Name</Label>
                    <Input value={form.admin_name} onChange={set("admin_name")} required data-testid="muni-admin-name-input" />
                  </div>
                  <div className="space-y-1">
                    <Label>Admin User ID</Label>
                    <Input type="email" value={form.admin_email} onChange={set("admin_email")} required data-testid="muni-admin-email-input" />
                  </div>
                  <div className="space-y-1">
                    <Label>Admin Password</Label>
                    <Input type="password" value={form.admin_password} onChange={set("admin_password")} required data-testid="muni-admin-password-input" />
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between border rounded-lg px-3 py-2.5">
                <div>
                  <p className="text-sm font-medium">Seed demo masters</p>
                  <p className="text-xs text-muted-foreground">Zones, wards, tax heads etc. (Bhilai-style starter)</p>
                </div>
                <Switch checked={form.seed_demo} onCheckedChange={(v) => setForm({ ...form, seed_demo: v })} data-testid="muni-seed-switch" />
              </div>
              <Button type="submit" className="w-full" disabled={saving} data-testid="muni-submit-button">
                {saving ? "Creating tenant database..." : "Onboard municipality"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="municipalities-grid">
        {items.map((m) => (
          <Card key={m.id} className="shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold">{m.name}</p>
                  <p className="text-sm text-muted-foreground">{m.district ? `${m.district}, ` : ""}{m.state} • Code: <span className="font-mono">{m.code}</span>{m.ulb_code ? <> • ULB Code: <span className="font-mono font-medium" data-testid={`muni-ulb-code-${m.code}`}>{m.ulb_code}</span></> : null}</p>
                  <p className="text-xs text-muted-foreground mt-1">Onboarded {fmtDate(m.onboarded_at)}</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <StatusBadge status={m.is_active ? "active" : "inactive"} />
                  <StatusBadge status={m.server_status} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                <div className="bg-muted rounded-lg py-2">
                  <p className="text-lg font-semibold tabular-nums">{m.stats?.properties ?? 0}</p>
                  <p className="text-[11px] text-muted-foreground">Properties</p>
                </div>
                <div className="bg-muted rounded-lg py-2">
                  <p className="text-lg font-semibold tabular-nums">{fmtINR(m.stats?.total_demand)}</p>
                  <p className="text-[11px] text-muted-foreground">Demand</p>
                </div>
                <div className="bg-muted rounded-lg py-2">
                  <p className="text-lg font-semibold tabular-nums">{fmtINR(m.stats?.collected)}</p>
                  <p className="text-[11px] text-muted-foreground">Collected</p>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-3 flex-wrap">
                <Button variant="outline" size="sm" onClick={() => setManageMuni(m)} data-testid={`muni-manage-admins-${m.code}`}>
                  <UserCog className="h-3.5 w-3.5 mr-1.5" /> Manage Admins
                </Button>
                <Button variant="outline" size="sm" onClick={() => setResetMuni(m)} data-testid={`muni-reset-admin-${m.code}`}>
                  <KeyRound className="h-3.5 w-3.5 mr-1.5" /> Reset Admin Password
                </Button>
                <Button variant="outline" size="sm" onClick={() => toggle(m)} data-testid={`muni-toggle-${m.code}`}>
                  {m.is_active ? "Deactivate" : "Activate"}
                </Button>
                <Button variant="outline" size="sm" onClick={() => navigate(`/super/data-cleanup?muni=${m.code}`)} data-testid={`muni-properties-${m.code}`}>
                  <Home className="h-3.5 w-3.5 mr-1.5" /> Properties / Data Cleanup
                </Button>
                <Button variant="outline" size="sm"
                  className="text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
                  onClick={() => setCleanupMuni(m)} data-testid={`muni-cleanup-${m.code}`}>
                  <Eraser className="h-3.5 w-3.5 mr-1.5" /> Data Cleanup
                </Button>
                {!m.is_active && (
                  <Button variant="outline" size="sm"
                    className="text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => setDeleteMuni(m)} data-testid={`muni-delete-${m.code}`}>
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {removed.length > 0 && (
        <div className="mt-8" data-testid="removed-municipalities-section">
          <div className="flex items-center justify-between gap-3 mb-3">
            <p className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
              <History className="h-4 w-4" /> Removed municipalities ({removed.length})
            </p>
            <Button variant="outline" size="sm" onClick={() => setShowRemoved((v) => !v)}
              data-testid="removed-toggle-button">
              {showRemoved ? <><EyeOff className="h-3.5 w-3.5 mr-1.5" /> Hide</> : <><Eye className="h-3.5 w-3.5 mr-1.5" /> Unhide</>}
            </Button>
          </div>
          {showRemoved && (
            <>
              <div className="space-y-2" data-testid="removed-municipalities-list">
                {removed.map((m) => (
                  <Card key={m.id} className="shadow-sm border-dashed">
                    <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <p className="font-medium">{m.name} <span className="font-mono text-xs text-muted-foreground">({m.code})</span></p>
                        <p className="text-xs text-muted-foreground">
                          Removed {fmtDate(m.deleted_at)}{m.deleted_by_name ? ` by ${m.deleted_by_name}` : ""} • data retained
                        </p>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => restore(m)} disabled={restoringId === m.id}
                        data-testid={`muni-restore-${m.code}`}>
                        <ArchiveRestore className="h-3.5 w-3.5 mr-1.5" />
                        {restoringId === m.id ? "Restoring..." : "Restore"}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">
                Restored municipalities come back as <span className="font-medium">Inactive</span> — activate them to re-enable logins.
              </p>
            </>
          )}
        </div>
      )}

      <AdminResetDialog muni={resetMuni} open={!!resetMuni} onOpenChange={(v) => !v && setResetMuni(null)} />
      <ManageAdminsDialog muni={manageMuni} open={!!manageMuni} onOpenChange={(v) => !v && setManageMuni(null)} />
      <DeleteMuniDialog muni={deleteMuni} onOpenChange={(v) => !v && setDeleteMuni(null)} onDeleted={load} />
      <CleanupMuniDialog muni={cleanupMuni} onOpenChange={(v) => !v && setCleanupMuni(null)} onDone={load} />
    </div>
  );
}
